import { formatDeviceId, type DeviceInfo, type GroupInfo, type OrganizationDetail } from '@viewer/protocol';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorText } from '../../lib/api/client';
import { useApi } from '../../lib/api/useApi';
import { Modal } from '../../ui/Modal';
import styles from './org.module.css';

interface Props {
  org: OrganizationDetail;
  onChanged(): Promise<void>;
}

export function GroupsTab({ org, onChanged }: Props) {
  const groups = useApi<GroupInfo[]>(`/api/orgs/${org.id}/groups`);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query, 300);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [devicesOf, setDevicesOf] = useState<GroupInfo | null>(null);
  const canManage = org.role !== 'member';

  const needle = debouncedQuery.trim().toLowerCase();
  const visible = groups.data?.filter((group) => group.name.toLowerCase().includes(needle));

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      await Promise.all([groups.reload(), onChanged()]);
    } catch (err) {
      setError(errorText(err));
    }
  }

  function rename(group: GroupInfo) {
    const next = prompt('Novo nome do grupo', group.name)?.trim();
    if (next && next !== group.name) void run(() => api.patch(`/api/orgs/${org.id}/groups/${group.id}`, { name: next }));
  }

  function remove(group: GroupInfo) {
    const extra = group.deviceCount
      ? ` Os ${group.deviceCount} computadores ficam sem grupo e quem só tinha acesso por ele perde o acesso.`
      : '';
    if (confirm(`Excluir o grupo "${group.name}"?${extra}`)) void run(() => api.delete(`/api/orgs/${org.id}/groups/${group.id}`));
  }

  return (
    <section className={styles.panel}>
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Grupos</h2>
        {canManage && (
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setCreating(true)}>
            + Criar grupo
          </button>
        )}
      </div>
      <p className={styles.help}>
        Grupos organizam os computadores (Loja 1, Loja 2, Escritório...) e são a forma mais simples de dar acesso: quem
        tem um grupo acessa todos os computadores dele.
      </p>
      <label className={`${styles.field} ${styles.searchField}`}>
        <span className={styles.label} id="group-search-label">
          Buscar grupo
        </span>
        <input
          className={styles.input}
          placeholder="Buscar grupo..."
          aria-labelledby="group-search-label"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {error && <p className={styles.error}>{error}</p>}
      {groups.error && <p className={styles.error}>{groups.error}</p>}
      {groups.data?.length === 0 && <p className={styles.empty}>Nenhum grupo ainda.</p>}
      {groups.data && groups.data.length > 0 && visible?.length === 0 && (
        <p className={styles.empty}>Nenhum grupo encontrado.</p>
      )}
      <div className={styles.list}>
        {visible?.map((group) => (
          <div key={group.id} className={styles.row}>
            <div className={styles.rowMain}>
              <span className={styles.rowName}>{group.name}</span>
              <span className={styles.rowMeta}>
                {group.deviceCount === 1 ? '1 computador' : `${group.deviceCount} computadores`}
              </span>
            </div>
            {canManage && (
              <div className={styles.rowActions}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}
                  onClick={() => setDevicesOf(group)}
                >
                  Dispositivos
                </button>
                <button type="button" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`} onClick={() => rename(group)}>
                  Renomear
                </button>
                <button type="button" className={`${styles.btn} ${styles.btnDanger} ${styles.btnSmall}`} onClick={() => remove(group)}>
                  Excluir
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {creating && (
        <CreateGroupDialog
          org={org}
          onClose={() => setCreating(false)}
          onCreated={() => run(async () => undefined)}
        />
      )}
      {devicesOf && (
        <GroupDevicesDialog
          org={org}
          group={devicesOf}
          onClose={() => setDevicesOf(null)}
          onChanged={() => run(async () => undefined)}
        />
      )}
    </section>
  );
}

function useDebounced(value: string, delayMs: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function CreateGroupDialog({
  org,
  onClose,
  onCreated,
}: {
  org: OrganizationDetail;
  onClose(): void;
  onCreated(): Promise<void>;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/orgs/${org.id}/groups`, { name: trimmed });
      await onCreated();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Modal title="Criar grupo" onClose={() => { if (!busy) onClose(); }}>
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        <label className={styles.field}>
          <span className={styles.label}>Nome do grupo</span>
          <input
            className={styles.input}
            placeholder="Loja 1, Escritório..."
            maxLength={80}
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.actions}>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={!name.trim() || busy}>
            {busy ? 'Criando...' : 'Criar grupo'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function GroupDevicesDialog({
  org,
  group,
  onClose,
  onChanged,
}: {
  org: OrganizationDetail;
  group: GroupInfo;
  onClose(): void;
  onChanged(): Promise<void>;
}) {
  const devices = useApi<DeviceInfo[]>(`/api/orgs/${org.id}/devices`);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query, 300);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const needle = debouncedQuery.trim().toLowerCase();
  const digits = needle.replace(/\D/g, '');
  const visible = (devices.data ?? [])
    .filter((device) => {
      if (!needle) return true;
      return (
        device.name.toLowerCase().includes(needle) ||
        formatDeviceId(device.code).toLowerCase().includes(needle) ||
        (digits.length > 0 && device.code.includes(digits))
      );
    })
    .sort(
      (a, b) =>
        Number(b.groupId === group.id) - Number(a.groupId === group.id) || a.name.localeCompare(b.name, 'pt'),
    );

  async function toggle(device: DeviceInfo) {
    if (pendingId) return;
    setPendingId(device.id);
    setError(null);
    try {
      await api.patch(`/api/orgs/${org.id}/devices/${device.id}`, {
        groupId: device.groupId === group.id ? null : group.id,
      });
      await Promise.all([devices.reload(), onChanged()]);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setPendingId(null);
    }
  }

  return (
    <Modal title={`Dispositivos de ${group.name}`} onClose={() => { if (!pendingId) onClose(); }}>
      <div className={styles.form}>
        <p className={styles.help}>
          Marque os computadores deste grupo. Se um computador já estiver em outro grupo, ele passa para este.
        </p>
        <label className={styles.field}>
          <span className={styles.label}>Buscar dispositivo</span>
          <input
            className={styles.input}
            placeholder="Buscar dispositivo..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        {error && <p className={styles.error}>{error}</p>}
        {devices.error && <p className={styles.error}>{devices.error}</p>}
        {devices.loading && !devices.data && <p className={styles.muted}>Carregando...</p>}
        {devices.data?.length === 0 && <p className={styles.empty}>Nenhum computador vinculado à empresa.</p>}
        {devices.data && devices.data.length > 0 && visible.length === 0 && (
          <p className={styles.empty}>Nenhum dispositivo encontrado.</p>
        )}
        <div className={styles.pickerList}>
          {visible.map((device) => {
            const checked = device.groupId === group.id;
            const elsewhere = !checked && device.groupName;
            return (
              <label key={device.id} className={styles.check}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={pendingId !== null}
                  onChange={() => void toggle(device)}
                />
                <span>
                  {device.name}
                  <span className={styles.muted}>
                    {' '}
                    · {formatDeviceId(device.code)}
                    {elsewhere ? ` · em ${device.groupName}` : ''}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
